/**
 * Everything about one role, on one screen.
 *
 * The console used to spread a single role across three tabs — actions,
 * modules, menus — each re-picking the role, re-fetching it and saving on its
 * own. Configuring a manager meant nine interactions and no point at which you
 * could see what a manager could actually do.
 *
 * Here you pick the role once on the left and edit all three on the right, with
 * a running summary of what will change and one Save that writes only the parts
 * you touched. Search filters every list, because a nineteen-item module list
 * and a two-hundred-item menu tree are not things anyone should scroll.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronDown, Copy, KeyRound, Layers3, ListTree, Loader2, Pencil, RotateCcw,
  Save, Search, ShieldCheck, Trash2,
} from 'lucide-react';
import { api } from '../services/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { EmptyState } from './common/States';
import { allMenuIds, buildMenuCatalog, normaliseMenuSelection } from '../lib/menuCatalog';
import { getModuleForTab } from '../lib/planAccess';
import { buildDraft, describeChanges, hasChanges, savePlan } from '../lib/roleAccessDraft';

const SECTIONS = [
  { id: 'permissions', label: 'Actions', icon: KeyRound,
    hint: 'The verbs this role may perform. Checked on every request.' },
  { id: 'modules', label: 'Modules', icon: Layers3,
    hint: 'Which parts of the product this role reaches. Enforced by the API.' },
  { id: 'menus', label: 'Menus', icon: ListTree,
    hint: 'What appears in the sidebar. Hides only — the module gate still applies.' },
];

const Toggle = ({ checked, disabled, onChange, children, tone = 'default' }) => (
  <label className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
    disabled ? 'opacity-50' : 'cursor-pointer'
  } ${checked
    ? 'border-primary bg-primary/10 text-primary font-medium'
    : 'border-border bg-background text-muted-foreground hover:bg-muted/50'
  } ${tone === 'muted' ? 'opacity-60' : ''}`}>
    <input type="checkbox" className="accent-primary" checked={checked} disabled={disabled}
      onChange={onChange} />
    {children}
  </label>
);

const Section = ({ section, open, onToggle, count, total, children }) => {
  const Icon = section.icon;
  return (
    <Card className="overflow-hidden">
      <button type="button" onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors">
        <Icon className="h-4 w-4 text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold">{section.label}</div>
          <div className="text-xs text-muted-foreground truncate">{section.hint}</div>
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Badge variant={count === total ? 'soft' : 'info'}>{count} of {total}</Badge>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open ? <CardContent className="pt-0 pb-4">{children}</CardContent> : null}
    </Card>
  );
};

const RoleAccessStudio = ({
  tenantId,
  canEditRole = () => true,
  onDuplicate,
  onEdit,
  onDelete,
  reloadToken = 0,
}) => {
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [roleSearch, setRoleSearch] = useState('');
  const [catalog, setCatalog] = useState({ actions: [], modules: [] });
  const [tenantModules, setTenantModules] = useState([]);
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({ permissions: true, modules: true, menus: false });
  const [filter, setFilter] = useState('');

  const MENU_CATALOG = useMemo(() => buildMenuCatalog(), []);
  const MENU_IDS = useMemo(() => allMenuIds(MENU_CATALOG), [MENU_CATALOG]);

  useEffect(() => {
    let dead = false;
    Promise.all([
      api.get(`/api/access-control/roles?tenant_id=${tenantId}`),
      api.get('/api/access-control/permissions'),
    ]).then(([r, p]) => {
      if (dead) return;
      const list = (r.data.roles || []).filter((role) => role.key !== 'superadmin');
      setRoles(list);
      setCatalog({ actions: p.data.actions || [], modules: p.data.modules || [] });
      setSelected((prev) => prev || list.find((role) => canEditRole(role.key))?.key || list[0]?.key || null);
    }).catch((e) => toast.error(e?.response?.data?.detail || 'Could not load roles'));
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- canEditRole is a stable predicate
  }, [tenantId, reloadToken]);

  const loadRole = useCallback(async (roleKey) => {
    if (!roleKey) return;
    setLoading(true);
    try {
      const [perms, modules, menus] = await Promise.all([
        api.get(`/api/access-control/roles/${roleKey}/permissions?tenant_id=${tenantId}`),
        api.get(`/api/access-control/roles/${roleKey}/modules?tenant_id=${tenantId}`),
        api.get(`/api/access-control/roles/${roleKey}/menus?tenant_id=${tenantId}`),
      ]);
      setTenantModules(modules.data.tenant_modules || []);
      const loaded = buildDraft({
        permissions: perms.data.effective || [],
        modules: modules.data.role_module_override,
        menus: menus.data.menu_access,
      });
      setOriginal(loaded);
      setDraft(buildDraft(loaded));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load this role');
      setOriginal(null); setDraft(null);
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadRole(selected); }, [selected, loadRole]);

  const dirty = original && draft ? hasChanges(original, draft) : false;
  const changes = original && draft ? describeChanges(original, draft) : [];
  const editable = selected ? canEditRole(selected) : false;
  const locked = !editable || loading;

  const pick = (roleKey) => {
    if (dirty && !window.confirm('Discard the unsaved changes to this role?')) return;
    setSelected(roleKey);
    setFilter('');
  };

  const setPart = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const toggleIn = (key, id, universe) => setDraft((prev) => {
    // null means unrestricted; ticking off the first item is what turns that
    // into a list, and it has to start from everything rather than from nothing.
    const current = prev[key] === null ? [...universe] : [...prev[key]];
    const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id];
    return { ...prev, [key]: key === 'menus' ? normaliseMenuSelection(next, MENU_CATALOG) : next };
  });

  const save = async () => {
    setSaving(true);
    const plan = savePlan(original, draft);
    try {
      for (const step of plan) {
        await api.put(
          `/api/access-control/roles/${selected}/${step.kind}`,
          { tenant_id: tenantId, ...step.body },
        );
      }
      toast.success(plan.length ? `Saved ${selected}` : 'Nothing to save');
      await loadRole(selected);
    } catch (e) {
      // Partial application is possible and worth saying so: the operator needs
      // to know the screen and the server may now disagree.
      toast.error(`${e?.response?.data?.detail || 'Could not save'} — reloading to show what applied`);
      await loadRole(selected);
    } finally { setSaving(false); }
  };

  const matches = (text) => !filter || String(text).toLowerCase().includes(filter.toLowerCase());
  const visibleRoles = roles.filter((r) =>
    !roleSearch || `${r.key} ${r.name || ''}`.toLowerCase().includes(roleSearch.toLowerCase()));

  const menuCount = draft?.menus === null ? MENU_IDS.length : (draft?.menus.length || 0);
  const moduleCount = draft?.modules === null ? tenantModules.length : (draft?.modules.length || 0);

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      {/* ── the roles ─────────────────────────────────────────────────── */}
      <Card className="h-fit lg:sticky lg:top-4">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)}
              placeholder="Find a role"
              className="w-full rounded-lg border border-border bg-background pl-8 pr-2 py-1.5 text-sm" />
          </div>
        </div>
        <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
          {visibleRoles.length === 0
            ? <EmptyState compact icon={ShieldCheck} title="No roles" description="Nothing matches that search." />
            : visibleRoles.map((role) => {
              const allowed = canEditRole(role.key);
              return (
                <button key={role.key} type="button" onClick={() => pick(role.key)}
                  className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors hover:bg-muted ${
                    selected === role.key ? 'bg-primary/10 border-l-2 border-l-primary' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{role.name || role.key}</span>
                    {!allowed ? <Badge variant="outline" className="ml-auto text-[10px]">read only</Badge>
                      : role.is_system ? null : <Badge variant="soft" className="ml-auto text-[10px]">custom</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono truncate">{role.key}</div>
                  {/* Managing the role and configuring it are the same job, so
                      the buttons are here rather than on a separate table that
                      had its own copy of this list. */}
                  {selected === role.key && (onDuplicate || onEdit || onDelete) ? (
                    <div className="flex gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                      {onDuplicate ? (
                        <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]"
                          onClick={() => onDuplicate(role.key)}>
                          <Copy className="h-3 w-3 mr-1" />Duplicate
                        </Button>
                      ) : null}
                      {!role.is_system && onEdit ? (
                        <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]"
                          onClick={() => onEdit(role)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                      ) : null}
                      {!role.is_system && onDelete ? (
                        <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] text-danger"
                          onClick={() => onDelete(role)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </button>
              );
            })}
        </CardContent>
      </Card>

      {/* ── the role ──────────────────────────────────────────────────── */}
      <div className="space-y-3 min-w-0">
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold truncate">
                {roles.find((r) => r.key === selected)?.name || selected || 'Pick a role'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {locked && !loading
                  ? 'This role ranks at or above your own — you can look, not change.'
                  : dirty ? changes.join(' · ') : 'No unsaved changes'}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {dirty ? (
                <Button variant="outline" size="sm" onClick={() => setDraft(buildDraft(original))}>
                  <RotateCcw className="h-4 w-4 mr-1.5" />Discard
                </Button>
              ) : null}
              <Button size="sm" onClick={save} disabled={!dirty || locked || saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                {saving ? 'Saving' : 'Save changes'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading || !draft ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading this role…
          </CardContent></Card>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input value={filter} onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter actions, modules and menus"
                className="w-full rounded-lg border border-border bg-background pl-8 pr-3 py-2 text-sm" />
            </div>

            {/* Actions */}
            <Section section={SECTIONS[0]} open={open.permissions}
              onToggle={() => setOpen((p) => ({ ...p, permissions: !p.permissions }))}
              count={draft.permissions.length} total={catalog.actions.length}>
              <div className="flex flex-wrap gap-2">
                {catalog.actions.filter(matches).map((action) => (
                  <Toggle key={action} disabled={locked} checked={draft.permissions.includes(action)}
                    onChange={() => setPart('permissions', draft.permissions.includes(action)
                      ? draft.permissions.filter((v) => v !== action)
                      : [...draft.permissions, action])}>
                    {action}
                  </Toggle>
                ))}
              </div>
            </Section>

            {/* Modules */}
            <Section section={SECTIONS[1]} open={open.modules}
              onToggle={() => setOpen((p) => ({ ...p, modules: !p.modules }))}
              count={moduleCount} total={tenantModules.length}>
              <label className="flex items-center gap-2 text-xs mb-3">
                <input type="checkbox" className="accent-primary" disabled={locked}
                  checked={draft.modules === null}
                  onChange={(e) => setPart('modules', e.target.checked ? null : [...tenantModules])} />
                Every module on the tenant's plan (no per-role restriction)
              </label>
              <div className="flex flex-wrap gap-2">
                {tenantModules.filter(matches).map((moduleKey) => (
                  <Toggle key={moduleKey} disabled={locked || draft.modules === null}
                    checked={draft.modules === null || draft.modules.includes(moduleKey)}
                    onChange={() => toggleIn('modules', moduleKey, tenantModules)}>
                    {moduleKey}
                  </Toggle>
                ))}
              </div>
            </Section>

            {/* Menus */}
            <Section section={SECTIONS[2]} open={open.menus}
              onToggle={() => setOpen((p) => ({ ...p, menus: !p.menus }))}
              count={menuCount} total={MENU_IDS.length}>
              <label className="flex items-center gap-2 text-xs mb-3">
                <input type="checkbox" className="accent-primary" disabled={locked}
                  checked={draft.menus === null}
                  onChange={(e) => setPart('menus', e.target.checked ? null : [...MENU_IDS])} />
                Every menu the plan allows (no menu restriction)
              </label>
              <div className="space-y-2">
                {MENU_CATALOG.map((group) => {
                  const children = group.children.filter((c) => matches(c.label) || matches(c.id));
                  const groupShown = matches(group.label) || matches(group.id) || children.length > 0;
                  if (!groupShown) return null;
                  const shown = children.length ? children : group.children;
                  const on = (id) => draft.menus === null || draft.menus.includes(id);
                  return (
                    <div key={group.id} className="rounded-lg border border-border p-2.5">
                      <Toggle disabled={locked || draft.menus === null} checked={on(group.id)}
                        onChange={() => setDraft((prev) => {
                          const current = prev.menus === null ? [...MENU_IDS] : [...prev.menus];
                          const ids = [group.id, ...group.children.map((c) => c.id)];
                          const turningOff = ids.some((id) => current.includes(id));
                          const next = turningOff
                            ? current.filter((v) => !ids.includes(v))
                            : [...new Set([...current, ...ids])];
                          return { ...prev, menus: normaliseMenuSelection(next, MENU_CATALOG) };
                        })}>
                        <span className="font-medium">{group.label}</span>
                        <span className="text-[10px] text-muted-foreground">{group.section}</span>
                      </Toggle>
                      {shown.length ? (
                        <div className="mt-2 pl-5 flex flex-wrap gap-1.5">
                          {shown.map((child) => {
                            const moduleKey = getModuleForTab(child.id);
                            const unlicensed = moduleKey && !tenantModules.includes(moduleKey);
                            return (
                              <Toggle key={child.id} disabled={locked || draft.menus === null}
                                checked={on(child.id)} tone={unlicensed ? 'muted' : 'default'}
                                onChange={() => toggleIn('menus', child.id, MENU_IDS)}>
                                {child.label}
                                {unlicensed ? <span className="text-[10px]">(unlicensed)</span> : null}
                              </Toggle>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

export default RoleAccessStudio;
